/* eslint-disable jsdoc/no-undefined-types */
/* eslint-disable jsdoc/require-param */
import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Paper,
  FormControlLabel,
  Switch,
  Box,
} from "@mui/material";

/**
 * A card component that displays the user's Growth Leader and Trainer status.
 *
 * It displays 3 switches, one for Growth Leader status, one for Trainer status,
 * and one to block the user from changing the Trainer status.
 *
 * @prop {object} dealer - The user's dealer object
 * @prop {bool} growthLeader - Whether the user is a Growth Leader
 * @prop {bool} isLeader - Whether the user is a Trainer
 * @prop {bool} isLeaderBlocked - Whether the user is blocked from changing Trainer status
 * @prop {Function} onGrowthLeaderChange - The function to call when the Growth Leader status changes
 * @prop {Function} onIsLeaderChange - The function to call when the Trainer status changes
 * @prop {Function} onIsLeaderBlockChange - The function to call when the block Trainer status changes
 * @returns {ReactElement} A card component with the user's status and 3 switches
 */
export default function StatusTogglesCard({
  dealer,
  growthLeader,
  isLeader,
  isLeaderBlocked,
  onGrowthLeaderChange,
  onIsLeaderChange,
  onIsLeaderBlockChange,
}) {
  return (
    <Card elevation={2} sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          User Status
        </Typography>
        
        <Stack spacing={3}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={growthLeader}
                  onChange={onGrowthLeaderChange}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Growth Leader Status
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {growthLeader && dealer.growth_leader_time
                      ? `Growth Leader since ${dealer.growth_leader_time}`
                      : "Not a Growth Leader"}
                  </Typography>
                </Box>
              }
            />
          </Paper>

          <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  disabled={isLeaderBlocked}
                  checked={isLeader}
                  onChange={onIsLeaderChange}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Trainer Status
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isLeader && dealer.is_leader
                      ? `Trainer since ${dealer.is_leader_date}`
                      : "Not a Trainer"}
                  </Typography>
                </Box>
              }
            />
          </Paper>

          <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isLeaderBlocked}
                  onChange={onIsLeaderBlockChange}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Block Trainer Status
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Prevent user from changing trainer status
                  </Typography>
                </Box>
              }
            />
          </Paper>
        </Stack>
      </CardContent>
    </Card>
  );
}
